import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, ArrowRight, Inbox } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/context/TenantContext";
import api, { formatApiError } from "@/lib/api";
import { SUBMISSION_STATUS } from "@/lib/strings";
import { toast } from "sonner";

const NEEDS_REVIEW = (c) =>
  ["SUBMITTED_BY_COMPANY", "UNDER_CONSULTANT_REVIEW"].includes(c.submission_status)
  || (c.to_verify || 0) > 0 || (c.open_requests || 0) > 0;

export default function AttivitaRevisione() {
  const navigate = useNavigate();
  const { changeCompany } = useTenant();
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/consultant/portfolio")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data.portfolio || [];
        setItems(list.filter(NEEDS_REVIEW));
      })
      .catch((e) => { toast.error(formatApiError(e)); setItems([]); });
  }, []);

  const open = (cid) => { changeCompany(cid); navigate("/dashboard"); };

  return (
    <div className="space-y-6" data-testid="attivita-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
          Attività da revisionare
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Aziende del tuo portafoglio con invii da revisionare, dati da verificare
          o richieste di integrazione aperte.
        </p>
      </div>
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-green-700" aria-hidden="true" />
            Coda di revisione
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!items ? (
            <p className="text-sm text-slate-500">Caricamento…</p>
          ) : items.length === 0 ? (
            <div className="text-center py-10" data-testid="attivita-empty">
              <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-3" aria-hidden="true" />
              <p className="text-sm text-slate-500">
                Nessuna attività in attesa: tutte le aziende sono in pari.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="attivita-table">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3 font-semibold">Azienda</th>
                    <th className="py-2 pr-3 font-semibold">Stato invio</th>
                    <th className="py-2 pr-3 font-semibold">Da verificare</th>
                    <th className="py-2 pr-3 font-semibold">Richieste aperte</th>
                    <th className="py-2 font-semibold text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => {
                    const st = SUBMISSION_STATUS[c.submission_status] || SUBMISSION_STATUS.DRAFT;
                    return (
                      <tr key={c.company_id} className="border-b border-slate-100"
                        data-testid={`attivita-row-${c.company_id}`}>
                        <td className="py-2.5 pr-3 font-medium text-slate-900">{c.denominazione}</td>
                        <td className="py-2.5 pr-3">
                          <Badge className={st.cls}>{st.label}</Badge>
                        </td>
                        <td className="py-2.5 pr-3 text-slate-600">{c.to_verify || 0}</td>
                        <td className="py-2.5 pr-3 text-slate-600">{c.open_requests || 0}</td>
                        <td className="py-2.5 text-right">
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => open(c.company_id)}
                            data-testid={`attivita-open-${c.company_id}`}>
                            Apri azienda <ArrowRight className="w-3 h-3 ml-1" aria-hidden="true" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
